/**
 * «الأدوار والصلاحيات» (ADR 0018): the organization's named staff roles. Each has a level and the sections
 * and actions it opens; granting it to people is done from their page (المستخدمون). Full administrators
 * only; the server checks again.
 */
import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import type { Permission, ProfileLevel, RoleProfile, RoleProfilesResponse } from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, errorText, send } from "../lib/api";
import { useCan, useOrg } from "../lib/org";
import { READY_ROLES, levelHint, levelLabel } from "../lib/permissions";
import { PERMISSION_LABEL, SECTIONS, type Section } from "../lib/sections";

/** Whether a page that has no actions of its own opens for this level (for its note in the list). */
const opensFor = (opensWith: Permission | "fullAdmin", level: ProfileLevel, open: Permission[]) =>
  opensWith === "fullAdmin" ? level === "org_admin" : open.includes(opensWith);

export function RolesPage() {
  const org = useOrg();
  const can = useCan();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<RoleProfile | "new" | null>(null);
  const { busy, error, run } = useSubmit();
  const data = useQuery({
    queryKey: ["role-profiles", org?.id],
    queryFn: () => api<RoleProfilesResponse>(`/v1/role-profiles?organizationId=${org!.id}`),
    enabled: !!org,
  });
  if (!org || data.isPending) return <Loading />;
  if (!can.fullAdmin || data.isError)
    return (
      <section className="card">
        <Empty title="هذه الصفحة لإدارة الجمعية بصلاحياتها الكاملة" />
      </section>
    );
  const { profiles, defaults } = data.data;
  const active = profiles.filter((p) => !p.archived);
  const archived = profiles.filter((p) => p.archived);

  function addReady() {
    void run(async () => {
      for (const r of READY_ROLES)
        if (!profiles.some((p) => p.name === r.name))
          await send("POST", "/v1/role-profiles", {
            organizationId: org!.id,
            name: r.name,
            level: r.level,
            permissions: defaults[r.level],
          });
      await qc.invalidateQueries({ queryKey: ["role-profiles"] });
    }, errorText);
  }
  function setArchived(p: RoleProfile, archived: boolean) {
    void run(async () => {
      await send("PATCH", `/v1/role-profiles/${p.id}`, { archived });
      await qc.invalidateQueries({ queryKey: ["role-profiles"] });
    }, errorText);
  }

  return (
    <>
      <PageTitle
        title="الأدوار والصلاحيات"
        sub="أدوار باسم تختاره، لكل دور الأقسام والإجراءات المسموحة له. يُمنح الدور من صفحة الشخص، على مجمع أو على الجمعية كلها."
        actions={
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Plus size={16} strokeWidth={1.75} /> دور جديد
          </button>
        }
      />
      <ErrorNote error={error} />
      {active.length === 0 ? (
        <section className="card">
          <Empty title="لا أدوار مخصصة بعد">
            ابدأ بالأدوار الجاهزة (مدير ومشرف، لكل المجمعات أو لمجمع) ثم عدّل صلاحياتها، أو أنشئ دورًا جديدًا.
          </Empty>
          <div className="form-actions">
            <button className="btn btn-secondary" disabled={busy} onClick={addReady}>
              {busy ? "جارٍ الإنشاء…" : "أنشئ الأدوار الجاهزة"}
            </button>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الدور</th>
                  <th>المستوى</th>
                  <th>الصلاحيات</th>
                  <th>الممنوح لهم</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <b>{p.name}</b>
                    </td>
                    <td>
                      <Badge tone="brand">{levelLabel[p.level]}</Badge>
                    </td>
                    <td className="num">{p.permissions.length}</td>
                    <td className="num">{p.holders}</td>
                    <td className="row-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(p)}>
                        تعديل
                      </button>
                      {p.holders === 0 && (
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={busy}
                          onClick={() => setArchived(p, true)}
                        >
                          أرشفة
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
      <section className="card">
        <div className="card-h">
          <h2>الأدوار الأساسية</h2>
        </div>
        <div className="section">
          <p className="muted small">
            «إدارة الجمعية» بلا دور مخصص لها كل الصلاحيات، وهي وحدها تعرّف الأدوار. «مدير المجمع» و«المشرف»
            بلا دور مخصص يعملان بالصلاحيات الافتراضية: المدير كل صلاحيات المجمع، والمشرف{" "}
            {defaults.supervisor.length} صلاحية (الخطط والحضور والتسميع والاختبارات والتقارير والسياسات).
            لتغييرها امنح الشخص دورًا مخصصًا.
          </p>
        </div>
      </section>
      {archived.length > 0 && (
        <section className="card">
          <div className="card-h">
            <h2>أدوار مؤرشفة</h2>
          </div>
          <ul className="list">
            {archived.map((p) => (
              <li key={p.id}>
                <span>
                  <b>{p.name}</b> <span className="muted small">{levelLabel[p.level]}</span>
                </span>
                <button
                  className="btn btn-ghost btn-sm"
                  disabled={busy}
                  onClick={() => setArchived(p, false)}
                >
                  استعادة
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {editing && (
        <ProfileModal
          organizationId={org.id}
          profile={editing === "new" ? null : editing}
          allowed={data.data.allowed}
          defaults={defaults}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function ProfileModal({
  organizationId,
  profile,
  allowed,
  defaults,
  onClose,
}: {
  organizationId: string;
  profile: RoleProfile | null;
  allowed: Record<ProfileLevel, Permission[]>;
  defaults: Record<ProfileLevel, Permission[]>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { busy, error, run } = useSubmit();
  const [name, setName] = useState(profile?.name ?? "");
  const [level, setLevel] = useState<ProfileLevel>(profile?.level ?? "supervisor");
  const [perms, setPerms] = useState<Set<Permission>>(new Set(profile?.permissions ?? defaults.supervisor));
  const open = allowed[level];

  function changeLevel(l: ProfileLevel) {
    setLevel(l);
    setPerms(new Set([...perms].filter((p) => allowed[l].includes(p))));
  }
  function toggle(p: Permission, on: boolean) {
    const next = new Set(perms);
    if (on) next.add(p);
    else next.delete(p);
    setPerms(next);
  }
  function toggleAll(keys: Permission[], on: boolean) {
    const next = new Set(perms);
    for (const k of keys) {
      if (on) next.add(k);
      else next.delete(k);
    }
    setPerms(next);
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    void run(async () => {
      const permissions = [...perms];
      if (profile) await send("PATCH", `/v1/role-profiles/${profile.id}`, { name: name.trim(), permissions });
      else await send("POST", "/v1/role-profiles", { organizationId, name: name.trim(), level, permissions });
      await qc.invalidateQueries({ queryKey: ["role-profiles"] });
      onClose();
    }, errorText);
  }

  return (
    <Modal title={profile ? `تعديل «${profile.name}»` : "دور جديد"} open onClose={onClose} wide>
      <form className="form" onSubmit={submit}>
        <ErrorNote error={error} />
        <Field id="p-name" label="اسم الدور" hint="مثل: مشرف تعليمي، منسق الاختبارات">
          <input id="p-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </Field>
        <Field id="p-level" label="المستوى" hint={levelHint[level]}>
          <select
            id="p-level"
            value={level}
            disabled={!!profile}
            onChange={(e) => changeLevel(e.target.value as ProfileLevel)}
          >
            {(["supervisor", "branch_manager", "org_admin"] as const).map((l) => (
              <option key={l} value={l}>
                {levelLabel[l]}
              </option>
            ))}
          </select>
        </Field>
        {profile && profile.holders > 0 && (
          <p className="muted small">
            ممنوح لـ{profile.holders} {profile.holders === 1 ? "شخص" : "أشخاص"}؛ تعديل الصلاحيات يسري عليهم
            فورًا.
          </p>
        )}
        <p className="muted small">
          الصلاحيات مرتبة كأقسام القائمة وصفحاتها. كل صفحة تظهر في القائمة لمن له صلاحيتها الأولى.
        </p>
        <div className="perm-groups">
          {SECTIONS.map((s: Section) => {
            const pages = s.pages
              .map((p) => ({ ...p, items: p.actions.filter((a) => open.includes(a.key)) }))
              .filter((p) => p.items.length > 0 || opensFor(p.opensWith, level, open));
            if (!pages.length) return null;
            const keys = pages.flatMap((p) => p.items.map((i) => i.key));
            const all = keys.length > 0 && keys.every((k) => perms.has(k));
            return (
              <fieldset key={s.id} className="perm-group">
                <legend>{s.label}</legend>
                {keys.length > 1 && (
                  <button type="button" className="perm-all" onClick={() => toggleAll(keys, !all)}>
                    {all ? "إلغاء الكل" : "تحديد الكل"}
                  </button>
                )}
                {pages.map((p) => {
                  const Icon = p.icon;
                  const own = p.actions.some((a) => a.key === p.opensWith);
                  return (
                    <div key={p.to} className="perm-page">
                      <div className="perm-page-title">
                        <Icon size={16} strokeWidth={1.75} aria-hidden="true" /> {p.label}
                      </div>
                      {p.items.map((i) => (
                        <label key={i.key} className="check">
                          <input
                            type="checkbox"
                            checked={perms.has(i.key)}
                            onChange={(e) => toggle(i.key, e.target.checked)}
                          />
                          {i.label}
                        </label>
                      ))}
                      {!own && (
                        <p className="muted small perm-note">
                          {p.opensWith === "fullAdmin"
                            ? "لمن له دور إدارة بكل الصلاحيات فقط"
                            : `تظهر لمن له: ${PERMISSION_LABEL[p.opensWith]}`}
                        </p>
                      )}
                    </div>
                  );
                })}
              </fieldset>
            );
          })}
        </div>
        <div className="form-actions">
          <button className="btn btn-primary" disabled={busy || name.trim().length < 2}>
            {busy ? "جارٍ الحفظ…" : profile ? "حفظ" : "إنشاء الدور"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
